import type { Asset, ScenarioOutput } from '@/types';
import { fmtDuration } from '@/lib/format';
import { severityColor, severityLabel } from '@/lib/color';
import './scenarios.css';

interface Props {
  preview: ScenarioOutput;
  assets: Asset[];
}

/**
 * Time-ordered cascade tree: each affected asset shown as a row with its
 * reason, propagation path, severity, and time-to-impact.
 */
export function CascadeViz({ preview, assets }: Props) {
  const byId = new Map(assets.map((a) => [a.asset_id, a]));
  const sorted = [...preview.affected].sort(
    (a, b) => a.affected_at_seconds - b.affected_at_seconds,
  );
  const max = sorted[sorted.length - 1]?.affected_at_seconds ?? 1;

  return (
    <div className="iris-cascade-viz">
      {sorted.map((n) => {
        const a = byId.get(n.asset_id);
        const left = max === 0 ? 0 : (n.affected_at_seconds / max) * 100;
        return (
          <div key={n.asset_id} className="iris-cascade-row">
            <div className="iris-cascade-time mono">
              {fmtDuration(n.affected_at_seconds)}
            </div>
            <div className="iris-cascade-bar">
              <div
                className="iris-cascade-bar-fill"
                style={{
                  left: 0,
                  width: `${left}%`,
                  background: severityColor(n.severity),
                  opacity: 0.4,
                }}
              />
              <div
                className="iris-cascade-marker"
                style={{
                  left: `${left}%`,
                  background: severityColor(n.severity),
                }}
              />
            </div>
            <div className="iris-cascade-meta">
              <div className="iris-cascade-name">
                <span>{a?.name ?? n.asset_id}</span>
                <span
                  className="iris-cascade-sev"
                  style={{
                    color: severityColor(n.severity),
                    background: `${severityColor(n.severity)}26`,
                  }}
                >
                  {severityLabel(n.severity)}
                </span>
              </div>
              <div className="caption">
                {n.reason} · loss {(n.loss_fraction * 100).toFixed(0)}% · path{' '}
                <span className="mono">{n.propagation_path.join(' → ')}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
